"use client";
import { ReactLenis, useLenis } from "lenis/react";
import { usePathname } from "next/navigation";
import { useEffect, type ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const OnChange = () => {
  return null;
};

function SmoothScrolling({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const lenis = useLenis();

  useEffect(() => {
    if (!lenis) return;

    gsap.registerPlugin(ScrollTrigger);
    gsap.ticker.lagSmoothing(0);

    const update = () => {
      ScrollTrigger.update();
    };

    lenis.on("scroll", update);

    const raf = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(raf);

    const refresh = () => ScrollTrigger.refresh();
    const rafId = requestAnimationFrame(refresh);
    const timeoutId = window.setTimeout(refresh, 100);
    window.addEventListener("load", refresh);
    document.fonts?.ready.then(refresh).catch(() => {});

    refresh();

    return () => {
      lenis.off("scroll", update);
      gsap.ticker.remove(raf);
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("load", refresh);
    };
  }, [lenis]);

  useEffect(() => {
    if (window.location.hash) {
      lenis?.scrollTo(window.location.hash, { immediate: true });
      return;
    }
    lenis?.scrollTo(0, { immediate: true });
  }, [pathname]);

  return (
    <ReactLenis root options={{ lerp: 0.1, duration: 1.5, smoothWheel: true }}>
      {children}
    </ReactLenis>
  );
}

export default SmoothScrolling;
